import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { format, parseISO } from 'date-fns'
import {
    ListItemText,
    Checkbox,
    Menu,
    MenuItem,
    Box,
    Container,
    Grid,
    Snackbar,
    Alert,
} from '@mui/material'
import { Tabs, Tab, Divider } from '@mui/material'
import DocumentUploader from '../../components/myEventDashboard/DocumentUploader'
import EventOverview from '../../components/myEventDashboard/overviewTab/EventOverview'
import ChatBotIcon from '../../components/common/ChatIcon' // Import the new component
import { getBackendUserUrl } from '../../utils/utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import duration from 'dayjs/plugin/duration' // Import duration plugin
import BookingsManager from '../../components/myEventDashboard/bookingsTab/BookingsManager'
import TeamTab from 'components/teamManagement/TeamTab'
import {
    CommonEventContextProvider,
    EventOperationsContextProvider,
} from '../../contexts/EventContext'
import EventCalendar from '../../components/calendar/EventCalendar'
import { UserContext } from 'contexts/UserContext'
import { buildAgendaItemPayload } from '../../utils/eventDetailsutils'
import DocumentList from '../../components/myEventDashboard/documentsTab/DocumentList'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(duration) // Extend dayjs with the duration plugin

// Add enum for tab values
const EventTab = {
    OVERVIEW: 'overview',
    BOOKINGS: 'bookings',
    AGENDA: 'agenda',
    TEAM: 'team',
    DOCUMENTS: 'documents',
}

const MyEventDashboard = () => {
    const { eventId } = useParams()
    const [hostCompanyId, setHostCompanyId] = useState(null)
    const [hostId, setHostId] = useState(null)
    const [eventOverviewDetails, setEventOverviewDetails] = useState(null)
    const [venueSpaces, setVenueSpaces] = useState(null)
    const [error, setError] = useState(null)
    const [addons, setAddons] = useState([])

    const [currentTab, setCurrentTab] = useState(EventTab.OVERVIEW)
    const [isAdditionalCardOpen, setAdditionalCardOpen] = useState(false)
    const [editableFields, setEditableFields] = useState({
        vision: 'A networking event connecting top industry leaders.',
        audience: 'Founders, VCs, Software Engineers',
        type: 'Corporate Networking, Panel Discussion',
    })

    const [timelineEvents, setTimelineEvents] = useState([])
    const [messageTags, setMessageTags] = useState([])
    const [highlightedSection, setHighlightedSection] = useState(null)
    const [open, setOpen] = useState(false)
    const [users, setUsers] = useState([])
    const [selectedUsers, setSelectedUsers] = useState([])
    const [participants, setParticipants] = useState([])
    const [anchorEl, setAnchorEl] = useState(null)
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [openAlert, setOpenAlert] = useState(false)
    const [refreshAgenda, setRefreshAgenda] = useState(0)
    const [documents, setDocuments] = useState([])

    const currentUserId = eventOverviewDetails?.event?.hostId
    const { user } = useContext(UserContext)

    // Moved autofillEventDetailsFromEntities from EventOverview to EventDetailsPage
    const autofillEventDetailsFromEntities = async (payload) => {
        // Parse out the different fields from the backend response
        const overview_page_metadata = payload.overview_page_metadata;
        const agenda_and_teams = payload.agenda_and_teams;
        // TODO: Store teamGroups for later use (e.g., in state or context)
        const teamGroups = agenda_and_teams?.teamGroups || [];
        // Prepare agendaSpaces for later use
        const agendaSpaces = agenda_and_teams?.agendaSpaces || [];

        // Existing logic for autofilling event details from overview_page_metadata
        console.log('autofillEventDetailsFromEntities', overview_page_metadata);
        setEventOverviewDetails((prev) => {
            if (!prev) return prev
            const entities = overview_page_metadata; // renamed for clarity
            const updated = { ...prev, event: { ...prev.event } }
            // Map extracted fields to event details fields
            if ((!updated.event.title || updated.event.title === '') && entities.eventTitle) {
                updated.event.title = entities.eventTitle
            }
            if ((!updated.event?.vision || updated.event?.vision === '') && entities?.vision) {
                updated.event.vision = entities.vision
            }
            if ((!updated.event?.guestCount || updated.event.guestCount === '') && entities.guestCount) {
                updated.event.guestCount = entities.guestCount
            }
            if ((!updated.event?.eventStartDate || updated.event?.eventStartDate === '') && entities?.startDateTime) {
                updated.event.eventStartDate = entities.startDateTime
            }
            if ((!updated.event?.eventEndDate || updated.event?.eventEndDate === '') && entities?.endDateTime) {
                updated.event.eventEndDate = entities.endDateTime
            }
            if ((!updated.event?.cost || updated.event?.cost === '') && entities?.costBudget) {
                updated.event.cost = entities.costBudget
            }
            if ((!updated.event?.demographics || updated.event?.demographics === '') && entities?.demographics) {
                updated.event.demographics = entities.demographics
            }
            if ((!updated.event?.marketing_strategy || updated.event?.marketing_strategy === '') && entities?.marketingStrategies) {
                updated.event.marketing_strategy = entities.marketingStrategies
            }
            if ((!updated.event?.keyActivities || updated.event?.keyActivities === '') && entities?.keyActivities) {
                updated.event.keyActivities = entities.keyActivities
            }
            if ((!updated.event?.specialFeatures || updated.event?.specialFeatures === '') && entities?.specialFeatures) {
                updated.event.specialFeatures = entities.specialFeatures
            }
            if ((!updated.event?.targetAudience || updated.event?.targetAudience === '') && entities?.targetAudience) {
                updated.event.targetAudience = entities.targetAudience
            }
            if ((!updated.event?.team || updated.event?.team === '') && entities?.team) {
                updated.event.team = entities.team
            }
            if ((!updated.event?.marketing_channels || updated.event?.marketing_channels === '') && entities?.marketingChannels) {
                updated.event.marketing_channels = entities.marketingChannels
            }
            // Add more mappings as needed
            return updated;
        });
        // For now, just log the parsed agenda and teamGroups for future use
        console.log('autofillEventDetailsFromEntities Parsed teamGroups (TODO):', teamGroups);
        console.log('autofillEventDetailsFromEntities Parsed agendaSpaces (TODO):', agendaSpaces);

        // Step 2: Create agenda spaces in backend
        (async () => {
            try {
                if (!eventId) {
                    console.error('No eventId found in CommonEventContext');
                    return;
                }
                const spaceNameToId = {};
                for (const [position, space] of agendaSpaces.entries()) {
                    try {
                        const res = await axios.post(getBackendUserUrl() + '/agenda/agenda_spaces/', {
                            event_id: eventId,
                            name: space.name,
                            // position is now optional
                        });
                        const spaceId = res.data.spaceId;
                        spaceNameToId[space.name] = spaceId;
                        console.log(`Created agenda space '${space.name}' with id ${spaceId}`);
                    } catch (err) {
                        console.error(`Failed to create agenda space: ${space.name}`, err);
                    }
                }
                // You can now use spaceNameToId for agenda item creation in the next step
                console.log('Agenda space name to ID mapping:', spaceNameToId);
                // Step 3: Create agenda items for each space
                for (const space of agendaSpaces) {
                    const spaceId = spaceNameToId[space.name];
                    if (!spaceId) continue;
                    for (const item of space.agendaItems || []) {
                        const payload = buildAgendaItemPayload({
                            eventId,
                            spaceId,
                            item,
                            userId: user?.userId
                        });
                        try {
                            await axios.post(getBackendUserUrl() + '/agenda/agenda_items/', payload);
                            console.log('Created agenda item:', payload);
                        } catch (err) {
                            console.error('Failed to create agenda item:', payload, err);
                        }
                    }
                }
                // After backend update, trigger refresh in EventCalendar
                setRefreshAgenda((prev) => prev + 1);
            } catch (err) {
                console.error('Error creating agenda spaces:', err);
            }
        })();
    }

    useEffect(() => {
        const fetchEventDetails = async () => {
            try {
                const apiUrl = `${getBackendUserUrl()}/event/event_details/${eventId}`
                const response = await axios.get(apiUrl)
                console.log('Event details response:', response.data)

                // Process addons
                // const processedAddons = response.data.event.addons
                //     ? response.data.event.addons.map((addon) => ({
                //           eventAddonsId: addon.eventAddonsId,
                //           addonType: addon.addonType,
                //           status: addon.status,
                //           lastSeenStatus: addon.lastSeenStatus,
                //           selected: addon.selected,
                //           price: addon.price,
                //           company: addon.company,
                //       }))
                //     : []

                // Set event overview details with processed addons
                setEventOverviewDetails(response.data)
                // setEventOverviewDetails({
                //     ...response.data,
                //     event: {
                //         ...response.data.event,
                //         addons: processedAddons,
                //     },
                // })

                // Set venue spaces
                setVenueSpaces(response.data.venue.spaces)

                // Set host ID and company ID from event data
                setHostId(response.data.event.hostId)

                console.log(
                    'Setting hostCompanyId:',
                    response.data.event.hostCompanyId
                )
                
                // Set addons array separately
                setAddons(response.data.event.addons)
                console.log('Event addons set:', response.data.event.addons)

                // Fetch timeline after setting event details
                fetchEventTimeline(eventId)

                const participants_response = await axios.get(
                    getBackendUserUrl() + `/event/${eventId}/participants`
                )
                if (participants_response.data) {
                    setParticipants(participants_response.data)
                }

                // Fetch venue spaces for the event
                const venueSpacesResponse = await axios.get(
                    `${getBackendUserUrl()}/event/get_event_spaces_for_event/${eventId}`
                )

                // Check if venueSpaces were found
                if (
                    venueSpacesResponse.data &&
                    venueSpacesResponse.data.venue_space_details
                ) {
                    const spaceDetails =
                        venueSpacesResponse.data.venue_space_details

                    // If spaceDetails are found, set them in state
                    if (spaceDetails.length > 0) {
                        setVenueSpaces(spaceDetails)
                    }
                }

                const hostCompanyId = response.data.event.hostCompanyId;
                if (hostCompanyId) {
                    setHostCompanyId(hostCompanyId);
                } else if (user?.userId) {
                    // hostCompanyId is null, fetch from backend using axios.post and getBackendUserUrl
                    try {
                        const userCompanyUrl = getBackendUserUrl() + `/user/user/${user.userId}/company`;
                        const res = await axios.get(userCompanyUrl);
                        if (res.data && res.data.companyId) {
                            setHostCompanyId(res.data.companyId);
                        } else {
                            setHostCompanyId(null); // or handle as needed
                        }
                    } catch (err) {
                        console.error(err);
                        setHostCompanyId(null); // or handle as needed
                    }
                } else {
                    setHostCompanyId(null); // or handle loading state
                }
            } catch (error) {
                console.error('Failed to fetch event details:', error)
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    console.error('Error data:', error.response.data)
                    console.error('Error status:', error.response.status)
                    console.error('Error headers:', error.response.headers)
                    setError(
                        `Failed to load event details: ${error.response.data.detail || error.response.statusText}`
                    )
                } else if (error.request) {
                    // The request was made but no response was received
                    console.error('Error request:', error.request)
                    setError('No response received from server')
                } else {
                    // Something happened in setting up the request that triggered an Error
                    console.error('Error message:', error.message)
                    setError(`Error: ${error.message}`)
                }
            }
        }

        if (eventId) {
            fetchEventDetails()
            console.log('participants', participants)
        }
    }, [eventId])

    useEffect(() => {
        const fetchCompanyUsers = async () => {
            try {
                const response = await axios.get(
                    getBackendUserUrl() + `/company/${hostCompanyId}/users`
                )
                setUsers(response.data)
            } catch (error) {
                console.error('Error fetching company users:', error)
            }
        }
        if (hostCompanyId) {
            fetchCompanyUsers()
        }
    }, [hostCompanyId])

    useEffect(() => {
        console.log('Updated users:', users)
    }, [users])

    const fetchEventTimeline = async (eventId) => {
        console.log('fetchEventTimeline', eventId)
        const apiUrl = `${getBackendUserUrl()}/event/event_timeline/${eventId}`
        const response = await axios.get(apiUrl)
        console.log('timelineEvents', response.data)
        setTimelineEvents(response.data)
    }

    const renderTabContent = (tabValue) => {
        console.log('Rendering tab:', tabValue);
        switch (tabValue) {
            case EventTab.OVERVIEW:
                return (
                    <Grid container spacing={4}>
                        <Grid item xs={12} id="overview-section">
                            <EventOverview
                                eventDetails={eventOverviewDetails}
                                setEventDetails={setEventOverviewDetails}
                                onAddTag={handleAddTag}
                                onAgendaTitleClick={() => {
                                    console.log('Agenda title clicked');
                                    setCurrentTab('agenda');
                                }}
                                venueSpaces={venueSpaces}
                            />
                        </Grid>
                    </Grid>
                )
            case EventTab.AGENDA:
                return (
                    <Grid item xs={12}>
                        <EventCalendar eventId={eventId} refreshAgenda={refreshAgenda} />
                    </Grid>
                )
            case EventTab.BOOKINGS:
                return (
                    <Grid item xs={12}>
                        <BookingsManager
                            eventDetails={eventOverviewDetails}
                            setEventDetails={setEventOverviewDetails}
                            venueSpaces={venueSpaces}
                        />
                    </Grid>
                )
            case EventTab.TEAM:
                return <TeamTab />
            case EventTab.DOCUMENTS:
                return (
                    <Grid item xs={12}>
                        <DocumentList documents={documents} eventId={eventId} />
                    </Grid>
                )
            default:
                return null
        }
    }

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue)
    }

    const handleAddTag = (tag) => {
        setMessageTags((prevTags) => [...prevTags, tag])
        handleTagNavigation(tag)
    }

    // Calculate the event duration in hours
    const calculateEventDuration = (start, end) => {
        const startDate = dayjs(start)
        const endDate = dayjs(end)
        const eventDuration = dayjs.duration(endDate.diff(startDate))
        return eventDuration.asHours().toFixed(1) // Get the duration in hours with one decimal point
    }

    const handleOpenAdditionalCard = () => {
        setAdditionalCardOpen(true)
    }

    const handleCloseAdditionalCard = () => {
        setAdditionalCardOpen(false)
    }

    const handleChange = (field) => (event) => {
        setEditableFields({
            ...editableFields,
            [field]: event.target.value,
        })
    }

    // const totalCost = (eventDetails.cost
    //   + eventDetails.addons.reduce((sum, addon) => sum + addon.price, 0)
    //   - eventDetails.sponsors.reduce((sum, sponsor) => sum + sponsor.price, 0)).toLocaleString();

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId)
        if (section) {
            // Determine which tab contains this section
            let tabValue = 'overview' // default
            if (sectionId === 'Event-Timeline-section') {
                tabValue = 'agenda'
            }
            // Add more conditions for other sections if needed

            // Change to the correct tab
            setCurrentTab(tabValue)

            // Use setTimeout to allow the tab content to render before scrolling
            setTimeout(() => {
                section.scrollIntoView({ behavior: 'smooth' })
            }, 100)
        }
    }

    useEffect(() => {
        // Make the scrollToSection function globally accessible
        window.scrollToSection = scrollToSection

        // Clean up
        return () => {
            delete window.scrollToSection
        }
    }, [])

    const handleTagNavigation = (tagName) => {
        console.log('Navigating to tag:', tagName)

        if (!tagName) {
            console.warn('No tag name provided for navigation')
            return
        }

        let targetTab = 'overview'
        let targetSection = null

        switch (tagName.toString().toLowerCase()) {
            // Overview Tab Sections
            case 'event basics':
            case 'date and time':
            case 'event vision':
            case 'event type':
                targetTab = 'overview'
                targetSection = 'Event-Basics-section'
                break

            case 'target audience':
            case 'audience information':
            case 'demographics':
                targetTab = 'overview'
                targetSection = 'audience-section'
                break

            case 'venue':
            case 'location':
                targetTab = 'overview'
                targetSection = 'venue-section'
                break

            case 'event format':
            case 'agenda overview':
                targetTab = 'overview'
                targetSection = 'format-section'
                break

            case 'speakers':
            case 'speakers and presenters':
                targetTab = 'overview'
                targetSection = 'speakers-section'
                break

            case 'marketing and promotion':
            case 'marketing strategy':
                targetTab = 'overview'
                targetSection = 'marketing-section'
                break

            // Other Tabs
            case 'agenda':
            case 'timeline':
                targetTab = 'agenda'
                targetSection = 'Event-Timeline-section'
                break

            case 'vendors':
                targetTab = 'vendors'
                break

            case 'tasks':
                targetTab = 'tasks'
                break

            default:
                targetTab = 'overview'
                break
        }

        // Switch to the correct tab
        setCurrentTab(targetTab)

        // Scroll to section after tab switch
        setTimeout(() => {
            if (targetSection) {
                const element = document.getElementById(targetSection)
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })
                    // Add highlight effect
                    element.style.backgroundColor = 'rgba(25, 118, 210, 0.1)'
                    setTimeout(() => {
                        element.style.backgroundColor = ''
                    }, 3000)
                }
            }
        }, 100)
    }

    const handleMenuClick = async (event) => {
        if (!hostCompanyId) {
            console.error('Host company ID is not set')
            setOpenAlert(true)
            return
        }
        setLoadingUsers(true)
        try {
            setAnchorEl(event.currentTarget)
        } catch (error) {
            console.error('Error fetching company users:', error)
        } finally {
            setLoadingUsers(false)
        }
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleUserSelect = (user) => {
        setSelectedUsers((prev) => {
            const isUserSelected = prev.some(
                (selectedUser) => selectedUser.userId === user.userId
            )
            if (isUserSelected) {
                return prev.filter(
                    (selectedUser) => selectedUser.userId !== user.userId
                )
            } else {
                return [...prev, user]
            }
        })
    }

    const handleRemoveParticipant = async (user) => {
        try {
            // Prevent removing self
            if (user.userId === currentUserId) {
                console.log('Cannot remove yourself from the event')
                return
            }

            await axios.delete(
                getBackendUserUrl() +
                    `/event/${eventId}/remove-participant/${user.userId}`
            )
            // Update participants list
            setParticipants((prev) =>
                prev.filter((p) => p.userId !== user.userId)
            )
        } catch (error) {
            console.error('Error removing participant:', error)
        }
    }

    const handleCloseAlert = (event, reason) => {
        if (reason === 'clickaway') {
            return
        }
        setOpenAlert(false)
    }

    if (error) return <div>{error}</div>
    if (!eventOverviewDetails) return <div>Loading event details...</div>

    return (
        <CommonEventContextProvider
            value={{
                hostCompanyId,
                hostId,
                eventId,
            }}
        >
            <EventOperationsContextProvider eventId={eventId}>
                <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                    {/* Event Title */}
                    {/* <Typography variant="h4" component="h1" gutterBottom>
                        {eventOverviewDetails.event.title}
                    </Typography> */}

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 300,
                                width: '300px',
                            },
                        }}
                    >
                        {users.map((user) => (
                            <MenuItem
                                key={user.userId}
                                onClick={() => handleUserSelect(user)}
                                disabled={participants.some(
                                    (p) => p.userId === user.userId
                                )}
                            >
                                <Checkbox
                                    checked={selectedUsers.some(
                                        (selectedUser) =>
                                            selectedUser.userId === user.userId
                                    )}
                                />
                                <ListItemText
                                    primary={`${user.firstName} ${user.lastName}`}
                                    secondary={
                                        participants.some(
                                            (p) => p.userId === user.userId
                                        )
                                            ? 'Already added'
                                            : null
                                    }
                                />
                            </MenuItem>
                        ))}
                        <Divider />
                    </Menu>


                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mt: 2,
                        }}
                    >
                    </Box>

                    {loadingUsers && <p>Loading users...</p>}

                    <Box sx={{ position: 'relative', mb: 4, minHeight: 56 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mb: 4,
                            }}
                        >
                            <Tabs
                                value={currentTab}
                                onChange={handleTabChange}
                                aria-label="event details tabs"
                                sx={{ mb: 4 }}
                            >
                                <Tab label="Overview" value={EventTab.OVERVIEW} />
                                <Tab label="Bookings" value={EventTab.BOOKINGS} />
                                <Tab label="Agenda" value={EventTab.AGENDA} />
                                <Tab label="Team" value={EventTab.TEAM} />
                                <Tab label="Documents" value={EventTab.DOCUMENTS} />
                            </Tabs>
                        </Box>
                        <Box
                            sx={{
                                position: { xs: 'static', md: 'absolute' },
                                right: { md: 0 },
                                top: { md: '50%' },
                                transform: { md: 'translateY(-50%)' },
                                mt: { xs: 2, md: 0 },
                                display: 'flex',
                                justifyContent: { xs: 'center', md: 'flex-end' },
                                width: { xs: '100%', md: 'auto' },
                            }}
                        >
                            <DocumentUploader
                                eventId={eventId}
                                onUploadComplete={autofillEventDetailsFromEntities}
                                setDocuments={setDocuments}
                            />
                        </Box>
                    </Box>

                    {renderTabContent(currentTab)}

                    <ChatBotIcon />

                    <Snackbar
                        open={openAlert}
                        autoHideDuration={6000}
                        onClose={handleCloseAlert}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleCloseAlert}
                            severity="warning"
                            sx={{ width: '100%' }}
                        >
                            Only collaborators within your company can be added,
                            please confirm you are in a company and/or have
                            collaborators to work with
                        </Alert>
                    </Snackbar>
                </Container>
            </EventOperationsContextProvider>
        </CommonEventContextProvider>
    )
}

export default MyEventDashboard
